import { useSettingsMenuProps } from 'global/cnr/contexts/useHelpers/useSettingsMenuProps';
import { useTempSettings } from 'global/cnr/contexts/useHelpers/useTempSettings';
import _ from 'lodash';
import React, { Suspense, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Segment } from 'semantic-ui-react';
import AppNotificationsManager, { appNotificationTypes } from '../appNotifications/AppNotificationsManager';
import ActionProvider, { ActionContext } from '../cnr/contexts/ActionContext';
import DataProvider from '../cnr/contexts/DataContext';
import { GlobalContext } from '../cnr/contexts/GlobalContext';
import { PreviewContext } from '../cnr/contexts/PreviewContext';
import { SettingParentContext } from '../cnr/contexts/SettingParentContext';
import { SettingsAreaContext } from '../cnr/contexts/SettingsAreaContext';
import ViewSettingsComponentProvider, { ViewSettingsComponentContext } from '../cnr/contexts/ViewSettingsComponentContext';
import { ViewSettingsContext } from '../cnr/contexts/ViewSettingsContext';
import ViewSettingsMenuProvider from '../cnr/contexts/ViewSettingsMenuContext';
import { helpTypes } from '../cnr/reducers/HelpReducer';
import { sortInitialState, sortReducer } from '../cnr/reducers/SortReducer';
import { uniqueKey } from '../common/keys';
import SuspenseDimmer from '../components/alerts/SuspenseDimmer';
import UiSaveButtons from '../components/buttons/UiSaveButtons';
import { gEnums } from '../enums/globalEnums';
import UiDataFilters, { fullFilterTypes } from '../pageItem/UiDataFilters';
import { getMenuItemMessage } from './components/menus/menuItemHelpers';
import { vsComponentTypes } from './enums/vsComponentTypes';
import AllHeaders from './headers/AllHeaders';
import { getSettingsContentType, useSwipeComponentType, useSwipeProps } from './helpers/viewSettingsHelpers';
import SegItemWrap from './wrappers/SegItemWrap';

const AppAccessRequests = React.lazy(() => import('../components/access/AppAccessRequests'));
const CardSettings = React.lazy(() => import("./create/createSubs/CardSettings"));
const ConfirmationQuestion = React.lazy(() => import('../components/alerts/ConfirmationQuestion'));
const CreateSomething = React.lazy(() => import("./create/CreateSomething"));
const DataConstraintBuilder = React.lazy(() => import("./create/createSubs/DataConstraintBuilder"));
const DeleteClient = React.lazy(() => import('./events/DeleteClient'));
const DeleteEvent = React.lazy(() => import('./events/DeleteEvent'));
const FormsList = React.lazy(() => import('./components/FormsList'));
const Help = React.lazy(() => import('../components/help/Help'));
const HomeGlobalSettings = React.lazy(() => import('./HomeGlobalSettings'));
const JsonPreview = React.lazy(() => import('../components/alerts/JsonPreview'));
const PropDropper = React.lazy(() => import('../dragNDrops/PropDropper'));
const SemFileViewer = React.lazy(() => import('../components/sem/SemFileViewer'));
const SettingsActionItems = React.lazy(() => import('./components/SettingsActionItems'));
const SettingsAreaMenu = React.lazy(() => import('./components/menus/SettingsAreaMenu'));
const SettingsConsole = React.lazy(() => import('./components/menus/SettingsConsole'));
const SettingsFormPage = React.lazy(() => import('./subComponents/SettingsFormPage'));
const TabbedDataView = React.lazy(() => import('../components/tables/TabbedDataView'));
const UploaderWithProvider = React.lazy(() => import('../components/uploading/Uploader'));
const ViewDataSidebar = React.lazy(() => import('./data/ViewDataSidebar'));

export const _swipeComponentTypes = {
  confirmationQuestion: 'confirmationQuestion',
  createSettingsPage: 'createSettingsPage',
  createSomething: 'createSomething',
  dataListPage: 'dataListPage',
  deleteClientPage: 'deleteClientPage',
  deleteEventPage: 'deleteEventPage',
  filePickerPage: 'filePickerPage',
  filesPage: 'filesPage',
  formListPage: 'formListPage',
  helpPage: 'helpPage',
  jsonPreview: 'jsonPreview',
  manifestPickerPage: 'manifestPickerPage',
  settingsContentWithWrap_fh: 'settingsContentWithWrap_fh',
  settingsContentWithWrap_fhau: 'settingsContentWithWrap_fhau',
  settingsContentWithWrap_vit: 'settingsContentWithWrap_vit',
  settingsContentWithWrap: 'settingsContentWithWrap',
  settingsFormPage_d: 'settingsFormPage_d',
  subContent: 'subContent',
  uploadPage: 'uploadPage',
}

export const _swipeContentTypes = {
  appAccessRequests: 'appAccessRequests',
  appUserSignInEmails: 'appUserSignInEmails',
  cardSettings: 'cardSettings',
  constraintBuilder: 'constraintBuilder',
  dataFilters: 'dataFilters',
  jsonViewer: 'jsonViewer',
  notificationsManager: 'notificationsManager',
  propOrganizeGrid: 'propOrganizeGrid',
  settingsActionItems: 'settingsActionItems',
  settingsFormPage: 'settingsFormPage',
  settingsMenuContainer_wrap_multi: 'settingsMenuContainer_wrap_multi',
  settingsMenuContainer_wrap: 'settingsMenuContainer_wrap',
  tabbedDataView: 'tabbedDataView',
}

export const _subContentTypes = {
  segItemWrap: 'segItemWrap',
  appAccessRequests: 'appAccessRequests',
  createSomething: 'createSomething',
  deleteClientPage: 'deleteClientPage',
  deleteEventPage: 'deleteEventPage',
}
// LOOK - make this lazy

export const newSwipeItem = (vsComponentType) => {
  return {
    key: vsComponentType
  }
}

const sortAllows = ['props', 'propSections', 'viewItems', 'views']
const arrangeAllows = ['props', 'views']
const _showConsole = false

/**
 * Contains the settings component(s) for the selected item
 * @returns a view for a group of settings
 * @description First child of ViewSettingsComponentProvider, which contains all the information
 * @description
 */
const ViewSettingsComponent = () => {

  const _vsts = gEnums.viewSettingTypes

  // settingsParentContext
  const settingsParentContext = useContext(SettingParentContext)
  const { handlers, states } = settingsParentContext ?? {}
  const { paps_state, appSettings_state, currentSettings_state } = states ?? {}

  // papsContext
  const { appArea } = paps_state ?? {}

  // pageContext  
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { settingsOptions, parentDefaults, groupingOptions } = aps_global ?? {}
  const { useDarkMode } = settingsOptions ?? {}
  const { useClientSignIn, useClientThemes, useHomeTransitions } = parentDefaults ?? {}
  const { useGlobalGroupSettings, useGlobalAlphabetSettings } = groupingOptions ?? {}

  // previewContext
  const previewContext = useContext(PreviewContext);
  const { preview_state, preview_handlers } = previewContext ?? {}
  const { settingsPreviewStatus } = preview_state ?? {}

  // settingsAreaContext
  const settingsAreaContext = useContext(SettingsAreaContext);
  const { settingsArea_state } = settingsAreaContext ?? {}
  const { saSplits, settingsOriginType, directViewItem } = settingsArea_state ?? {}

  // globalContext
  const globalContext = useContext(GlobalContext)
  const { global_state } = globalContext ?? {}
  const { settings_globalPageItems } = global_state ?? {}

  const { settingsPreviewOn } = appSettings_state ?? {}

  // viewSettingsContext
  const viewSettingsContext = useContext(ViewSettingsContext)
  const { viewSettings_state } = viewSettingsContext ?? {}
  const { testItem, settings_temp, showPermissions, helpKey, settings_preview_changed, settings_preview, currentFormData } = viewSettings_state ?? {}

  // **********************************************************
  // this is set in ViewSettingsComponentReducer and contains the information needed to fill the component
  // **********************************************************
  // viewSettingsComponentContext  
  const viewSettingsComponentContext = useContext(ViewSettingsComponentContext);
  const { vsc_state } = viewSettingsComponentContext ?? {}
  const { swipedItem, isGlobal, data_current, updateProps, callOrigin, buttonPermissions } = vsc_state ?? {}

  const _swipedItem = _.cloneDeep(swipedItem)

  // actionContext
  const actionContext = useContext(ActionContext)
  const { action_state } = actionContext ?? {}
  const { questionProps, preview } = action_state ?? {}

  const { _global, _viewItems } = useTempSettings(settings_temp, isGlobal)

  // local state
  const [sortOk, setSortOk] = useState()
  const [showSort] = useState()
  const [sortedItems, setSortedItems] = useState()
  const [propSections, setPropSections] = useState()

  let viewItems = _viewItems

  // this is unique to settings_globalPageItems  
  if (_swipedItem && _swipedItem.key === vsComponentTypes.globalPageItems && settings_globalPageItems) {
    viewItems = settings_globalPageItems
  }

  // local state
  const [sort_state] = useReducer(sortReducer, sortInitialState());

  /**
   * useSwipeProps
   */
  const vscProps = useSwipeProps(
    data_current,
    _swipedItem,
    updateProps,
    isGlobal,
    appArea,
    callOrigin,
  );

  console.log('vscProps', vscProps)

  const { formProps, viewProps, isMulti, isStatic, restrictedAccess } = vscProps ?? {}
  const { formHandler } = formProps ?? {}

  const swipeComponent_type = useSwipeComponentType(_swipedItem, {
    questionProps,
    preview,
    formHandler,
    handlers
  });

  const settingsContent_type = useMemo(
    () => getSettingsContentType(_swipedItem, { isMulti }),
    [_swipedItem, isMulti]
  );

  _showConsole && console.log('vsc_state', swipeComponent_type, settingsContent_type, vsc_state)

  useEffect(() => {
    // if settings_preview_changed, update the preview 
    if (settings_preview && settingsPreviewOn) {
      preview_handlers.handleSettings_preview(settings_preview, settingsPreviewOn, settingsPreviewStatus)
    }
    // eslint-disable-next-line
  }, [settings_preview_changed]);

  const setSortedReady = (nx, propSections) => {
    setSortOk(true)
    setSortedItems(nx)
    if (propSections) { setPropSections(propSections) }
  }

  /**
   * 
   * @param {*} key 
   * @param {*} _viType  
   * @returns the header for the item based on the key, _viType and button permissions
   */
  const itemHeader = (key, _viType, _bpms) => {
    const stateProps = {
      sortOk,
      propSections,
      sortedItems,
    }
    if (_bpms) { _bpms.formActions = formProps.formActions }
    if (!_bpms) { _bpms = buttonPermissions }
    const headerProps = { key, viType: _viType, buttonPermissions: _bpms, stateProps, data_current, restrictedAccess, caption: _swipedItem.caption ? _swipedItem.caption : null }
    return <AllHeaders dataHeaderType={gEnums.dataHeaderTypes.itemHeader} headerProps={headerProps} />
  }


  /** Returns the `FormsList` component */
  const formListPage = () => {
    const _viType = _swipedItem.key
    buttonPermissions.allowAdd = true
    formProps.header = itemHeader(_swipedItem.key, _viType, buttonPermissions)
    const pageContent = <FormsList />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.createSettings })
  }

  /** Returns the `CreateSomething` component */
  const createSomething = (key) => {
    formProps.header = itemHeader(key)
    const pageContent = <CreateSomething createOrigin={gEnums.createOriginTypes.viewSettings} createType={key} createVit={'clients'} />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.createEvent })
  }

  /** Returns the `CreateSomething` component */
  const createSettingsPage = () => {
    formProps.header = itemHeader('add')
    const pageContent = <CreateSomething createOrigin={gEnums.createOriginTypes.createSettings} createType={'add'} />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.createSettings })
  }

  /** Returns the `DeleteClient` component */
  const deleteClientPage = () => {
    formProps.header = itemHeader('deleteClient')
    return SegItemWrap(formProps.header, <DeleteClient />, null, { vsct: vsComponentTypes.deletePage })
  }

  /** Returns the `DeleteEvent` component */
  const deleteEventPage = () => {
    formProps.header = itemHeader('deleteEvent')
    return SegItemWrap(formProps.header, <DeleteEvent />, null, { vsct: vsComponentTypes.deletePage })
  }

  /** Returns the `ViewData` component within a `DataProvider` */
  const dataListPage = () => {
    const _viType = _swipedItem.key
    formProps.header = itemHeader(_swipedItem.key, _viType)
    const itemListData = _global[_swipedItem.key] ? _global[_swipedItem.key] : {}
    const ups = { key: _swipedItem.key, isList: true }
    const pageContent = <ViewDataSidebar viewItem={{ key: _viType }} isList={true} />
    return <DataProvider itemListData={itemListData} isSettingData={false} updateProps={ups} >
      {SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.dataPage })}
    </DataProvider>
  }

  /** Returns the `SemFileViewer` component for manifest files */
  const manifestPickerPage = () => {
    formProps.header = itemHeader('selectManifestIcon')
    const { fieldProps } = _swipedItem
    const pageContent = <SemFileViewer
      key={uniqueKey('vsc', 'mpp')}
      fieldProps={fieldProps}
      fileMode={gEnums.fileModes.view}
      storageType={gEnums.storageTypes.manifest}
      noMid={true}
    />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.manifestPickerPage })
  }

  /** Returns the `SemFileViewer` component for selecting files */
  const filePickerPage = () => {
    formProps.header = itemHeader('selectFile')
    const { fieldProps } = _swipedItem
    const pageContent = <SemFileViewer
      key={uniqueKey('vsc', 'fpp')}
      fieldProps={fieldProps}
      fileMode={gEnums.fileModes.viewWithPick}
      noMid={true}
    />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.filePickerPage })
  }

  /** Returns the `SemFileViewer` component for uploading files */
  const filesPage = (storageType) => {
    formProps.header = itemHeader(storageType, storageType)
    const pageContent = <SemFileViewer
      allowUpload={true}
      formProps={formProps}
      storageType={storageType}
      fileMode={gEnums.fileModes.view}
      noMid={true}
    />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.filesPage })
  }

  /** Returns the `FormPage` component */
  const settingsFormPage = (subKey, vpsa, diKey, message) => {
    // appSettings_handlers.handleAmmend_appSettingsCalls(subKey, 'SettingsFormPage')  
    return <SettingsFormPage formProps={formProps} swipedItem={_swipedItem} subKey={subKey} vpsa={vpsa} diKey={diKey} message={message} />
  }

  /** Returns the `Help` component */
  const helpPage = () => {
    formProps.header = itemHeader('help')
    const { formKey, pit, psg, vit } = updateProps ?? {}
    let hsProps = {}
    if (pit) {
      hsProps = { helpDocField: 'pr', helpDocSubField: formKey }
    } else if (psg) {
      hsProps = { helpDocField: 'ps', helpDocSubField: formKey }
    } else {
      if (isGlobal && !vit) {
        hsProps = { helpDocField: 'vw', helpDocSubField: formKey }
      } else {
        hsProps = { helpDocField: 'vi', helpDocSubField: formKey }
      }
    }
    if (!hsProps.helpDocSubField) { hsProps.helpDocSubField = vsc_state.swipedItem.settingKey }
    return <Help key={uniqueKey('vsc', 'hp', helpKey)} helpType={helpTypes.documents} hsProps={hsProps} groupItems={viewProps} />
  }

  /** Returns the `PropDropper` component */
  const propOrganizeGrid = (_type) => {
    const upsSections = { ...updateProps }
    upsSections.formKey = 'propSections'
    return <PropDropper />
  }

  /** Returns the `Uploader` component */
  const uploadPage = () => {
    formProps.header = itemHeader('upload')
    const pageContent = <UploaderWithProvider
      origin={'viewSettingsComponent'}
      swipedItem={_swipedItem}
    />
    return SegItemWrap(formProps.header, pageContent, null, { vsct: vsComponentTypes.uploadPage })
  }

  const copyPage = () => {
    formProps.header = itemHeader('copy')
    return SegItemWrap(formProps.header, <Segment>[{'TEST'}</Segment>, null, { vsct: vsComponentTypes.uploadPage })
  }

  /** This is the first function that gets hit after the initial grid is loaded */
  const settingsMenuContainer = (menuItems, menuTypeKey, getColors, menuType, otherData) => {
    const pips = { getColors, menuType, menuItems, menuTypeKey, otherData }
    const sortProps = { setSortedReady, showSort, sort_state }
    return <ViewSettingsMenuProvider pips={pips} sortProps={sortProps} />
  }

  /** Retuns `settingsMenuContainer` after setting the `menuItems` */
  const settingsMenuContainer_wrap = (menuTypeKey) => <ViewSettingsMenuProvider menuTypeKey={menuTypeKey} />

  /** Return the content based on the `subKey` */

  /**
   * Return the content based on the `subKey`, If the subKey exists
   * @param {string} subKey 
   * @param {object} buttonPermissions 
   * @returns 
   */
  const subContent = (subKey) => {

    formProps.subKey = subKey

    const _viType = subKey

    buttonPermissions.allowHelp = true

    formProps.header = itemHeader(subKey, _viType, buttonPermissions)

    switch (subKey) {
      case _vsts.appData:
        return SegItemWrap(formProps.header, settingsMenuContainer(viewItems, subKey, false, gEnums.menuTypes.viewItemData))

      case vsComponentTypes.appAccessRequests:
        return <AppAccessRequests />

      case vsComponentTypes.actions:
      case vsComponentTypes.viewActions:
        return SegItemWrap(formProps.header, <SettingsActionItems actionOrigin={'viewSettingsComponent'} />)

      case vsComponentTypes.createEvent:
      case vsComponentTypes.updateEvent:
      case vsComponentTypes.eventUpdates:
        return createSomething(subKey)

      case vsComponentTypes.createClient:
        return createSomething(subKey)

      case vsComponentTypes.deleteClient:
        return deleteClientPage()

      case vsComponentTypes.deleteEvent:
        return deleteEventPage()

      case vsComponentTypes.globalSettings:
      case vsComponentTypes.groupSettings:
      case vsComponentTypes.pageSettings:
      case vsComponentTypes.pageItemSettings:
      case vsComponentTypes.propSettings:
      case vsComponentTypes.propSectionSettings:
      case vsComponentTypes.dataModes:
        return SegItemWrap(formProps.header, <SettingsConsole vsComponentType={subKey} />)

      default:
        console.log('subKey', subKey)
        return SegItemWrap(formProps.header, settingsFormPage(subKey), null, { viType: _viType, vsct: vsComponentTypes.subPage })
    }
  }

  /** Return the content based on the `key` */
  const settingsContent = (key) => {

    let { formKey } = updateProps ?? {}
    if (!formKey) { formKey = key }

    const _viType = formKey

    const { vit } = updateProps ?? {}
    const viewItem_current = viewItems && viewItems[vit] ? viewItems[vit] : null

    formProps.header = !directViewItem && itemHeader(key, _viType)

    console.log('settingsContent_type', settingsContent_type)

    switch (settingsContent_type) {
      case _swipeContentTypes.appAccessRequests:
        return <AppAccessRequests />

      case _swipeContentTypes.appUserSignInEmails:
        return <div>{'appUserSignInEmails'}</div>

      case _swipeContentTypes.cardSettings:
        return SegItemWrap(formProps.header, <CardSettings content={settingsFormPage(key)} useDarkMode={useDarkMode} />, null, { viType: _viType, vsct: vsComponentTypes.card })

      case _swipeContentTypes.constraintBuilder:
        return <DataConstraintBuilder viewItem_current={viewItem_current} useDarkMode={useDarkMode} />

      case _swipeContentTypes.dataFilters:
        return <UiDataFilters fullFilterType={fullFilterTypes.settingsView} viewItem_current={viewItem_current} />

      case _swipeContentTypes.jsonViewer:
        break;

      case _swipeContentTypes.notificationsManager:
        return <AppNotificationsManager appNotificationType={appNotificationTypes.notificationsManager} forSettings={true} useDarkMode={true} />

      case _swipeContentTypes.propOrganizeGrid:
        return propOrganizeGrid(key)

      case _swipeContentTypes.settingsActionItems:
        // anything with an action will be directed here
        return <SettingsActionItems isGlobal={isGlobal} actionOrigin={'viewSettingsComponent'} viType={_viType} />

      case _swipeContentTypes.settingsMenuContainer_wrap_multi:
        return settingsMenuContainer_wrap(key)

      case _swipeContentTypes.settingsMenuContainer_wrap:
        return settingsMenuContainer_wrap(key)

      case _swipeContentTypes.tabbedDataView:
        break;

      // case _swipeContentTypes.settingsFormPage:
      default:
        buttonPermissions.allowHelp = true
        formProps.header = itemHeader(key, _viType, buttonPermissions)
        return settingsFormPage(null, null, null, getMenuItemMessage(key, { useClientSignIn, useClientThemes, useHomeTransitions, useGlobalGroupSettings, useGlobalAlphabetSettings }))

    }

  }

  /**
   * 
   * @param {string} caption 
   * @param {function} onClickFunction 
   * @param {string} dataKey 
   * @returns 
   */
  const footer = (caption, oc, dataKey) => <UiSaveButtons
    save={{ oc: updateFromFooter, caption }}
    clickOptions={{ oc, dataKey }}
  />

  /**
   * handles the click from the save button
   * @param {object} clickOptions 
   */
  const updateFromFooter = (clickOptions) => {
    if (clickOptions && clickOptions.oc) {
      clickOptions.oc(_global[_swipedItem.key], _swipedItem.key, currentFormData)
    }
  }

  /**
   * Catch all wrapper
   * @param {string} key  
   * @param {boolean} forForm 
   * @returns the content wrapped in a SegItemWrap
   */
  const settingsContentWithWrap = (key, forForm, footer) => {
    const pageContent = settingsContent(key)
    let flexName = 'hc'
    const cnn = 'setz setz-content ' + key
    return SegItemWrap(formProps.header, pageContent, null, { flexName: flexName, vsct: 'key', forForm: forForm }, footer, null, null, null, cnn)
  }

  /**
   * 
   * @returns the content of the swiped item after the initial load
   */
  const swipeComponent = () => {

    const { key, subKey, dKey, diKey } = _swipedItem

    const { settingsUpdateHandler, settingsUpdateHandlerFunction } = formHandler ?? {}
    const handler = handlers ? handlers[settingsUpdateHandler] : {}
    const handlerFn = handler ? handler[settingsUpdateHandlerFunction] : {}

    switch (swipeComponent_type) {
      case _swipeComponentTypes.confirmationQuestion:
        return <ConfirmationQuestion />

      case _swipeComponentTypes.createSettingsPage:
        return createSettingsPage()

      case _swipeComponentTypes.createSomething:
        return createSomething(key)

      case _swipeComponentTypes.dataListPage:
        return dataListPage()

      case _swipeComponentTypes.deleteClientPage:
        return deleteClientPage()

      case _swipeComponentTypes.deleteEventPage:
        return deleteEventPage()

      case _swipeComponentTypes.filePickerPage:
        return filePickerPage()

      case _swipeComponentTypes.filesPage:
        return filesPage(subKey)

      case _swipeComponentTypes.formListPage:
        return formListPage()

      case _swipeComponentTypes.helpPage:
        return helpPage()

      case _swipeComponentTypes.jsonPreview:
        return <JsonPreview />

      case _swipeComponentTypes.manifestPickerPage:
        return manifestPickerPage()

      case _swipeComponentTypes.settingsContentWithWrap:
        return settingsContentWithWrap(key, true)

      case _swipeComponentTypes.settingsContentWithWrap_fh:
        return settingsContentWithWrap(key, true)

      case _swipeComponentTypes.settingsContentWithWrap_fhau:
        const caption = settingsUpdateHandlerFunction.replace('handle', '')
        return settingsContentWithWrap(
          key,
          true,
          footer(_.startCase(caption), handlerFn, key)
        )

      case _swipeComponentTypes.settingsContentWithWrap_vit:
        return settingsContentWithWrap(key)

      case _swipeComponentTypes.settingsFormPage_d:
        return settingsFormPage(dKey, null, diKey, 'test')

      case _swipeComponentTypes.subContent:
        return subContent(subKey)

      case _swipeComponentTypes.uploadPage:
        return uploadPage()

      default:
        return settingsContentWithWrap(key, true)
    }
  }

  /**
   * 
   * @returns a component
   * @description Returns a component. Most are swipe base, others (helpPage and copyPage) are static,
   */
  const content_main = () => {

    if (showPermissions) {
      return <HomeGlobalSettings isGlobal={isGlobal} />
    } else if (helpKey) {
      return helpPage()
    } else if (testItem) {
      return copyPage()
    } else if (showSort) {
      return swipeComponent()
    } else {
      if (_swipedItem) {
        if (_swipedItem.sortedItems) {
          return swipeComponent()
        } else {
          if (_swipedItem.key) {
            return swipeComponent()
          }
        }
      } else {
        switch (settingsOriginType) {
          case gEnums.settingsOriginTypes.app:
            return <SettingsAreaMenu />
          default:
            return <div>{'settingsMenuContainer'}</div>
        }
      }
    }
    return <div></div>
  }

  if ((formProps) || isStatic) {
    return <Suspense fallback={<SuspenseDimmer origin={'View Settings Component'} />}>
      {content_main()}
    </Suspense>
  }

  return <Segment inverted>{'No Props'}</Segment>

}

const ViewSettingsComponentWithProvider = (props) => {

  console.log('>>> ViewSettingsComponentWithProvider')

  const { ncProps } = props

  const settingsAreaContext = useContext(SettingsAreaContext);
  const { settingsArea_state } = settingsAreaContext ?? {}
  const { directViewItem } = settingsArea_state ?? {}

  const viewSettingsContext = useContext(ViewSettingsContext);
  let { viewSettings_state } = viewSettingsContext ?? {}
  let { settings_temp, settings_temp_global } = viewSettings_state

  const {
    callOrigin,
    appArea,
    addHelp,
    editProps,
    isGlobal, // yes
    swipedItem, // yes
    swipedItems, // yes
    swipeToSelected, // yes  
  } = ncProps

  const dataRoot = swipedItem?.isDataRoot ? swipedItem.viewListData : null

  const settingsMenuProps = useSettingsMenuProps({ swipedItem, swipedItems, isGlobal, settings_temp, settings_temp_global })

  const { updateProps, data_current, buttonPermissions } = settingsMenuProps ?? {}

  const vsc_state = {
    directViewItem,
    data_current,
    buttonPermissions,
    dataRoot,
    editProps,
    isGlobal,
    swipedItem,
    swipedItems,
    updateProps,
    appArea,
    callOrigin
  }
  const vsc_handlers = { addHelp, swipeToSelected }

  return <ViewSettingsComponentProvider
    vsc_state={vsc_state}
    vsc_handlers={vsc_handlers}
  >
    <ActionProvider>
      <ViewSettingsComponent />
    </ActionProvider>
  </ViewSettingsComponentProvider>
}

export default ViewSettingsComponentWithProvider